import { useSliderContext, useSliderStateContext } from '@contexts/slider'
import { ReactComponent as ChevronIcon } from '@icons/chevron.svg'
import { ReactComponent as CloseIcon } from '@icons/close.svg'
import { desktop, modifier, tablet } from '@styles/mixins'
import { colors, grid } from '@styles/vars'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'

export default function Slider({ children, src, gallery }: Props) {
    const [visible, setVisible] = useState(false)

    const state = useSliderStateContext()
    const slider = useSliderContext()

    useEffect(() => {
        switch (state) {
            case "visible":
                setVisible(true)
                break
            case "hiding":
                setVisible(false)
                break
            default:
                break
        }
    }, [state])
    
    return (
        <Wrapper visible={visible}>
            <Background onClick={slider.close} />
            <Close onClick={slider.close}><CloseIcon /></Close>
            {gallery && (
                <Navigation>
                    <button className="prev" onClick={gallery.prev}><ChevronIcon /></button>
                    <button className="next" onClick={gallery.next}><ChevronIcon /></button>
                </Navigation>
            )}
            <Container>
                <Content>
                    <img src={src}></img>
                    {children}
                </Content>
            </Container>
        </Wrapper>
    )    
}

// STYLES
const Navigation = styled.div`
    button {
        display: block;
        position: absolute;
        top: 0;
        bottom: 0;
        width: 30px;
        transition: .2s ease-out;
        border: 0;
        cursor: pointer;
        background: hsla(0, 0%, 0%, 0);
        padding: 0;

        ${tablet(css`
            width: 90px;
        `)}
        
        svg {
            fill: #fff;
            width: 30px;
            height: 30px;
        }

        &:hover {
            background: hsla(0, 0%, 0%, .4);
        }
        
        &.prev {
            left: 0;

            svg {
                transform: rotate(90deg);
            }
        }

        &.next {
            right: 0;

            svg {
                transform: rotate(-90deg);
            }
        }
    }
`

const Close = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    width: 90px;
    height: 90px;
    cursor: pointer;
    z-index: 10;

    display: flex;
    align-items: center;
    justify-content: center;
    transition: .2s ease-out;

    &:hover {
        background: hsla(0, 0%, 0%, .2);
    }

    svg {
        fill: ${colors.white};
    }
`

const Content = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: auto;

    ${tablet(css`
        height: 100%;
    `)}

    img {
        max-width: 100%;
        max-height: 80%;
        margin-bottom: 20px;
        object-fit: contain;
    }

    * {
        color: ${colors.white};
    }
`

const Background = styled.div`
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: hsla(0, 0%, 0%, 0.8);
    opacity: 0;
    transition: .2s ease-out;
`

const Container = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(0.95);
    margin-right: -50%;
    opacity: 0;
    transition: .2s ease-out;
    height: 100%;
    max-width: calc(100vw - ${grid.margins.phone} * 2);

    ${tablet(css`
        max-width: calc(100vw - ${grid.margins.tablet} * 2);
    `)}

    ${desktop(css`
        max-width: calc(100vw - ${grid.margins.desktop} * 2);
    `)}

    display: flex;
    justify-content: center;
    align-items: center;
`

const Wrapper = styled.div<{ visible: boolean }>`
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 9;

    ${modifier("visible", css`
        ${Background} {
            opacity: 1;
        }

        ${Container} {
            opacity: 1;
            transform: translate(-50%, -50%);
        }
    `)}
`

// TYPES
interface Props {
    children: React.ReactNode
    src?: string
    gallery?: Interfaces.Gallery
}